import { Hidden } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import LaunchIcon from '@material-ui/icons/Launch';
import { Redirect, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { ArcRoutes } from '../../../constants/routes';
import { useGroupsBillingSettings } from '../../../hooks/useGroupsBillingSettings';
import { GuidedProcessSupportProps, SearchProps } from '../../../models/arc-epi-props';
import { BillingAccount } from '../billing-accounts-models';
import { GroupsState } from '../../groups/groups-model';
import { ExternalLinks } from '../../../../../global/models/lov-proc-model';
import { ExternalLink } from '../../../../../global/components/external-link-modal';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { ApiEndpoint } from '../../../../../global/constants/api-endpoints';
import { downloadFileFromApi } from '../../../../../global/helpers/file-helpers';
import { DomoLink } from '../../../../../global/components/domo-report-link';

interface Props {
	groupsBillingSettingsId: string;
}

const ReportsBillingAccount: React.FC<Props> = (props: Props) => {

	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const buttonClasses = buttonStyles();

	const groupSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.GroupSearch}`);
	const groupsState = useSelector<TcsmStates, GroupsState>(state => state.groupsState);
	const token = useSelector<TcsmStates, string>(state => state.authenticatedState?.accessToken);

	const externalLinks = useSelector<TcsmStates, ExternalLinks>(state => state.lovProcState.externalLinks);

	const routeMatch = useRouteMatch();

	// Select a group if none has been selected.
	if (!groupsState?.currentGroup?.group_id) {
		return (
			<Redirect to={{
				pathname: groupSelect,
				state: {
					actionLink: routeMatch.path,
				},
			}} />);
	}

	// Select billing account.
	const billingAccountSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}`);
	const selectedBillingAccount = useSelector<TcsmStates, BillingAccount>(state => state.billingAccountsState?.currentBillingAccount);

	if (!selectedBillingAccount) {
		return (
			<Redirect to={{
				pathname: billingAccountSelect,
				state: {
					actionLink: routeMatch.path,
					finalAction: routeMatch.path,
				},
			}} />);
	}

	const useGroupsBillingSettingsLoaded = useGroupsBillingSettings(props.groupsBillingSettingsId);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const groupsProps = epiProps?.groupsBillingSettingsState?.epiContent;

	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);

	if (!useGroupsBillingSettingsLoaded) {
		return <LoadingCircle />;
	}

	function CompleteReportItemLink(linkUrl: string): string {
		return linkUrl.replace('=:group', `=${groupsState?.currentGroup?.group_id}`);
	}

	function DownloadDocument() {
		if (!groupsState?.currentGroup?.group_id || !selectedBillingAccount?.account_number) return null;

		const requestUrl = `${ApiEndpoint.mlps}/group/${groupsState?.currentGroup?.group_id}/accounts/${selectedBillingAccount?.account_number}.xlsx`;

		downloadFileFromApi(requestUrl, token, 'employee-list.xlsx');
	}

	function RenderReportItem(linkText: string, linkUrl: string) {

		if (!linkUrl) return null;

		if (linkUrl.includes(ApiEndpoint.domoUrl)) {
			return <li><DomoLink domoLink={linkUrl} linkText={linkText} /></li>;
		}

		return (
			<>
				<li><ExternalLink newWindow linkIcon={<LaunchIcon />} linkText={!linkText ? linkUrl : linkText} linkUrl={linkUrl} /></li>
			</>
		);
	}

	function RenderDownloadDocument(linkText: string) {

		return (
			<li>
				<button
					type='button'
					className={buttonClasses.linkButton}
					onClick={DownloadDocument}>
					<svg aria-hidden='true' focusable='false'>
						<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#excel-icon' />
					</svg>
					<span>{linkText}</span>
				</button>
			</li>
		);
	}

	function RenderContent() {
		return (
			<ul className={buttonClasses.linkButtonList} >
				{RenderReportItem(groupsProps.reportsBenefitsGroupViewChangeTitle, CompleteReportItemLink(externalLinks.change_report_page))}
				{RenderReportItem(groupsProps.reportsBenefitsGroupViewEnrollmentTitle, CompleteReportItemLink(`${ApiEndpoint.domoUrl}report_name=MLPSTCSMEnrollment&group_id=:group`))}
				{RenderReportItem(groupsProps.reportsBenefitsGroupViewAnnualEnrollmentTitle, CompleteReportItemLink(`${ApiEndpoint.domoUrl}report_name=MLPSTCSMAnnualEnrollment&group_id=:group`))}
				{RenderDownloadDocument(searchSettings?.downloadEmployeeListButtonText)}
			</ul>
		);
	}

	return (
		<PageContainer>
			<MainContent>
				<PageHeader headerText={groupsProps.reportsBillingAccountTitle} description={groupsProps.reportsBillingAccountDescription} />
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.billingAccountReportsGpsTitle} contents={gpsSettings?.billingAccountReportsGps} />
					</SideBarContent>
				</Hidden>
				<RenderContent />
			</MainContent>

			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsSettings?.billingAccountReportsGpsTitle} contents={gpsSettings?.billingAccountReportsGps} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
};

export default ReportsBillingAccount;
